import { Injectable } from '@angular/core';

import {
    DisplayType,
    OptionStatus,
    Participant,
    ParticipantMarketStatus,
    ParticipantMarketType,
    ParticipantOption,
    ParticipantPriceStatus,
    ParticipantPriceType,
    ParticipantStatus,
    ParticipantType,
    Price,
} from '@cds/betting-offer';
import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { Nullable, StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import {
    FixtureParticipantPriceMap,
    ForecastModel,
    ForecastPlaces,
    V3FixtureParticipantPriceMap,
    WinGameFixture,
    WinGameParticipant,
} from '@frontend/sports/racing/feature/models';

import { HorsesFixtureHelper, PricingStateFixture } from './horses-fixture-helper.service';

@Injectable({
    providedIn: 'root',
})
export class HorsesFixturePricingHelper {
    /**
     * Return a dictionary of corresponding price for each participant
     * Only returns prices that are in visible or suspended state
     *
     * @param fixture
     * @param marketType
     * @returns hash of: <participantId, price>
     * @memberOf HorsesFixturePricingHelper
     */
    getPrices(fixture: WinGameFixture, marketType: ParticipantMarketType, preferredPriceType?: ParticipantPriceType): FixtureParticipantPriceMap {
        const result = {} as FixtureParticipantPriceMap;
        for (const participant of fixture.participants) {
            const options = participant.options.filter((o) => o.marketType === marketType);
            const fixtureParticipantId = participant.id;
            if (options.length === 0) {
                result[fixtureParticipantId] = undefined;
            } else if (options.length === 1) {
                result[fixtureParticipantId] = options[0];
            } else {
                result[fixtureParticipantId] = this.getOption(options, marketType, preferredPriceType);
            }
        }

        return result;
    }

    getOptionPrices(fixture: WinGameFixture, optionGroup: EventOptionGroup, preferredPriceType?: ParticipantPriceType): V3FixtureParticipantPriceMap {
        const result = {} as V3FixtureParticipantPriceMap;
        const fliteredOptions =
            (optionGroup.isStartingPriceAvailable && optionGroup.isWinPriceAvailable) ||
            optionGroup.detailedGrouping.displayType !== DisplayType.Wincast
                ? optionGroup.options
                : optionGroup.options.filter(
                      (o) =>
                          (o.participantPriceType === ParticipantPriceType.StartingPrice && optionGroup.isStartingPriceAvailable) ||
                          (o.participantPriceType === ParticipantPriceType.Fixed && optionGroup.isWinPriceAvailable),
                  );

        for (const participant of fixture.participants) {
            const options = fliteredOptions.filter((o) => o.optionPlayerId === participant.id);
            if (options && options.length > 0) {
                if (options && options.length === 1) {
                    // options[0].online = this.isDerivedMarketOptionLocked(options[0]);
                    result[participant.id] = options[0];
                } else {
                    result[participant.id] = this.filterParticipantOption(options, preferredPriceType);
                }
            }
        }

        return result;
    }

    isAvailableWinOption(option: ParticipantOption | undefined): boolean {
        return !!(
            option &&
            option.marketType === ParticipantMarketType.Win &&
            option.marketStatus === ParticipantMarketStatus.Visible &&
            option.participantPriceStatus === ParticipantPriceStatus.Visible &&
            (option.priceType === ParticipantPriceType.StartingPrice || NativePrice.fromNativePriceElement(option.price).greaterThan(1))
        );
    }

    isV3WinOptionAvailable(option: EventOption | undefined): boolean {
        return !!(
            option &&
            option.status === OptionStatus.Visible &&
            (option.participantPriceType === ParticipantPriceType.StartingPrice || option.nativePrice.greaterThan(1))
        );
    }

    isPriceLocked<TParticipant extends Pick<Participant, 'status'>>(
        fixture: PricingStateFixture,
        participant: TParticipant | null,
        option: ParticipantOption | undefined,
    ): boolean {
        if (!HorsesFixtureHelper.isTradable(fixture)) {
            // If fixture is finished or resulted
            return true;
        }

        if (!participant || participant.status !== ParticipantStatus.NotDefined) {
            // If the participant is not participating
            return true;
        }

        if (this.isOptionLocked(option)) {
            // if option and market are not visible
            return true;
        }

        return false;
    }

    isDerivedMarketPriceLocked<TParticipant extends Pick<Participant, 'status'>>(
        fixture: PricingStateFixture,
        participant: TParticipant | null,
        option: Nullable<EventOption>,
    ): boolean {
        if (!HorsesFixtureHelper.isTradable(fixture)) {
            // If fixture is finished or resulted
            return true;
        }

        if (!participant || participant.status !== ParticipantStatus.NotDefined) {
            // If the participant is not participating
            return true;
        }

        if (this.isDerivedMarketOptionLocked(option)) {
            // if option and market are not visible
            return true;
        }

        return false;
    }

    getPriceForMarketType(participant: Participant, marketType: ParticipantMarketType): ParticipantOption[] {
        return participant.options.filter((o) => o.marketType === marketType);
    }

    isPriceStatusVisible(participant: Participant, marketType: ParticipantMarketType): boolean {
        return this.isPriceStatus(participant, marketType, ParticipantPriceStatus.Visible) && participant.status === ParticipantStatus.NotDefined;
    }

    isDerivedMarketPriceStatusVisible(participant: Participant, marketType: ParticipantMarketType): boolean {
        return participant.status === ParticipantStatus.NotDefined;
    }

    allPricesAreLocked<TParticipant extends Pick<WinGameParticipant, 'id' | 'status'>>(
        fixture: PricingStateFixture,
        participants: TParticipant[],
        options: FixtureParticipantPriceMap,
    ): boolean {
        for (const participant of participants) {
            const option = options[participant.id];
            if (option && !this.isPriceLocked(fixture, participant, option)) {
                return false;
            }
        }

        return true;
    }

    getPriceHistory(option: ParticipantOption | undefined): Price[] {
        let priceHistory: Price[] = [];
        if (option && option.priceType === ParticipantPriceType.Fixed && option.priceHistory && option.priceHistory.length) {
            // if any price history >= 10 take only 2 because there's not enough space to show 3.
            const numberOfPrices = option.priceHistory.some((pr) => NativePrice.fromNativePriceElement(pr).greaterThanOrEqualTo(10)) ? 2 : 3;
            priceHistory = option.priceHistory.slice(-numberOfPrices);
        }

        return priceHistory;
    }

    getV3PriceHistory(option: EventOption | undefined): Price[] {
        let priceHistory: Price[] = [];
        if (option && option.participantPriceType === ParticipantPriceType.Fixed && option.priceHistory && option.priceHistory.length) {
            // if any price history >= 10 take only 2 because there's not enough space to show 3.
            priceHistory = option.priceHistory.slice(-3);
        }

        return priceHistory;
    }

    allPricesAreSP<TParticipant extends Pick<WinGameParticipant, 'id'>>(options: FixtureParticipantPriceMap, participants: TParticipant[]): boolean {
        for (const participant of participants) {
            const option = options[participant.id];
            if (!option || option.priceType !== ParticipantPriceType.StartingPrice) {
                return false;
            }
        }

        return true;
    }

    isFixedOptionParticipant(participant: Participant): boolean {
        const options = participant.options.filter((o) => o.marketType === ParticipantMarketType.Win);
        const validOption = this.selectValidWinOption(options);

        return !!validOption && validOption.priceType === ParticipantPriceType.Fixed;
    }

    private getOption(
        options: ParticipantOption[],
        marketType: ParticipantMarketType,
        preferredPriceType?: ParticipantPriceType,
    ): ParticipantOption | undefined {
        if (typeof preferredPriceType !== 'undefined') {
            const preferredOption = options.find((o) => o.priceType === preferredPriceType);
            if (preferredOption && this.isVisibleOption(preferredOption)) {
                return preferredOption;
            }
        }
        if (marketType === ParticipantMarketType.Win) {
            return this.selectValidWinOption(options);
        } else {
            return options[0] || null;
        }

        return undefined;
    }

    private selectValidWinOption(options: ParticipantOption[]): ParticipantOption | undefined {
        const fixedPriceOption = options.find((o) => o.priceType === ParticipantPriceType.Fixed);
        const startingPriceOption = options.find((o) => o.priceType === ParticipantPriceType.StartingPrice);
        if (fixedPriceOption && (this.isVisibleOption(fixedPriceOption) || !startingPriceOption)) {
            // If fixed price available and either is valid or starting price does not exists
            return fixedPriceOption;
        }
        if (startingPriceOption && this.isVisibleOption(startingPriceOption)) {
            // When no valid fixed price and existing starting price return SP
            return startingPriceOption;
        }

        return fixedPriceOption || startingPriceOption;
    }

    private isPriceStatus(participant: Participant, marketType: ParticipantMarketType, priceStatus: ParticipantPriceStatus): boolean {
        return participant.options.some((p) => p.marketType === marketType && p.participantPriceStatus === priceStatus);
    }

    private isVisibleOption(option: Nullable<ParticipantOption>): boolean {
        return !!(
            option &&
            option.marketStatus === ParticipantMarketStatus.Visible &&
            option.participantPriceStatus === ParticipantPriceStatus.Visible &&
            (option.priceType !== ParticipantPriceType.Fixed || NativePrice.fromNativePriceElement(option.price).greaterThan(1))
        );
    }

    private isOptionLocked(option: ParticipantOption | undefined): boolean {
        return (
            !option ||
            option.marketStatus !== ParticipantMarketStatus.Visible ||
            option.participantPriceStatus !== ParticipantPriceStatus.Visible ||
            (option.priceType === ParticipantPriceType.Fixed && NativePrice.fromNativePriceElement(option.price).lessThanOrEqualTo(1))
        );
    }

    private isDerivedMarketOptionLocked(option: Nullable<EventOption>): boolean {
        return !option || !option.visible || (option.participantPriceType === ParticipantPriceType.Fixed && option.nativePrice.lessThanOrEqualTo(1));
    }

    getParticipantOptions(id: number, participantOptionGroups: StringDictionary<EventOption[]>, preferredPriceType: ParticipantPriceType) {
        const partOptions = participantOptionGroups[id];

        return this.filterParticipantOption(partOptions, preferredPriceType);
    }

    filterParticipantOption(eventOptions: EventOption[], preferredPriceType?: ParticipantPriceType) {
        const preferredOption = eventOptions ? eventOptions.find((option) => option.participantPriceType === preferredPriceType) : undefined;
        if (preferredOption?.status === OptionStatus.Visible && preferredOption.online) {
            return preferredOption;
        } else {
            return eventOptions ? eventOptions.filter((option) => option.participantPriceType === ParticipantPriceType.StartingPrice)[0] : undefined;
        }
    }

    someOptionsPriced(
        runners: WinGameParticipant[],
        participantOptionGroups: {
            [id: string]: EventOption[];
        },
    ): boolean {
        return runners
            .filter((t) => t.properties?.type !== ParticipantType.Favourite)
            .flatMap((t) => this.getParticipantOptions(t.id, participantOptionGroups, ParticipantPriceType.Fixed))
            .some((t) => t?.nativePrice.greaterThan(1));
    }

    isPlaceAssigned(model: ForecastModel, isTricast?: boolean): boolean {
        if (!isTricast) {
            return model.first || model.second;
        }

        return model.third || model.fourth || model.fifth;
    }

    getMarketTypeByKey(
        key: string,
        forecastColumns: {
            id: keyof ForecastPlaces;
            name: string;
            visible: boolean;
        }[],
    ): ParticipantMarketType {
        if (forecastColumns.some((c) => c.id === key)) {
            return ParticipantMarketType.Forecast;
        }

        return ParticipantMarketType.Tricast;
    }
}
